// Service pour l'inventaire utilisant fetch natif
import {
  getOrdersByLocationAndDateRange,
  getProductsByLocation,
} from "./shopifyApi";
import { OrderLineItem, ProductBatch } from "../types/shopify";
import { RootState, store } from "../store";
import { handleUnauthorizedError } from "./apiMiddleware";

// URL de base de l'API
const API_BASE_URL = `${
  import.meta.env.VITE_API_URL || "http://localhost:5000/api"
}/inventory`;

// Fonction pour récupérer le token d'authentification depuis le store Redux
const getAuthToken = (): string | null => {
  const state = store.getState() as RootState;
  return state.user.currentUser?.token || null;
};

// Fonction pour générer les en-têtes avec le token d'authentification
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

// Interface pour un changement d'inventaire
export interface InventoryChange {
  id: string;
  price?: number;
  qte: number; // quantité réellement ajoutée/soustraite au stock (commercialisable)
  name: string;
  vendor?: string;
  sku?: string;
  previousQuantity?: number;
  newQuantity?: number;
  previousStatusQuantities?: Record<string, number>;
  newStatusQuantities?: Record<string, number>;
  stateChanges?: Array<{
    status: 'marketable' | 'reserved' | 'defective';
    previousQuantity: number;
    newQuantity: number;
    variation: number;
  }>;
  externalCode?: string;
  barcode?: string;
  // Champs optionnels pour les entrées rattachées à un bon de commande
  nearExpiryQty?: number; // quantité de lots bientôt périmés
  defectiveQty?: number; // quantité non commerciable (défectueuse)
  missingQty?: number; // quantité manquante par rapport au PO
  commercialQty?: number; // quantité commercialisable (copie de qte pour lisibilité côté reporting)
  poQty?: number; // quantité attendue depuis le PO
  // Détail des lots ajoutés/sortis pour ce produit lors de cette opération
  batches?: Array<ProductBatch & { status?: 'marketable' | 'reserved' | 'defective' }>;
}

// Interface pour un log d'inventaire
export interface InventoryLog {
  _id?: string;
  date: Date;
  location: string; // Champ renommé de locationId en location selon la normalisation du système
  changes: InventoryChange[];
  type: "entry" | "exit" | "inventory" | "revert" | "transfer";
  connectionId?: string;
  comment?: string;
  createdAt?: Date;
  updatedAt?: Date;
  userId?: string;
  purchaseOrderId?: string;
}

// Interface pour le résumé d'inventaire
export interface InventorySummary {
  totalProducts: number;
  totalValue: number;
  lowStockCount: number;
  outOfStockCount: number;
}

// Interface pour l'état actuel de l'inventaire
export interface InventoryItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  vendor: string;
  sku: string;
  external_id: string;
}

// Interface pour la réponse de l'API résumé
export interface InventorySummaryResponse {
  inventory: InventoryItem[];
  summary: InventorySummary;
}

// Obtenir tous les logs d'inventaire
export const getAllInventoryLogs = async (): Promise<InventoryLog[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/logs`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la récupération des logs:", error);
    throw error;
  }
};

// Obtenir les logs d'inventaire par type
export const getInventoryLogsByType = async (
  type: string
): Promise<InventoryLog[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/logs/type/${type}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la récupération des logs de type ${type}:`,
      error
    );
    throw error;
  }
};

// Obtenir les logs d'inventaire par date
export const getInventoryLogsByDate = async (
  startDate: Date,
  endDate: Date
): Promise<InventoryLog[]> => {
  try {
    const url = new URL(`${API_BASE_URL}/logs/date`);
    url.searchParams.append("startDate", startDate.toISOString());
    url.searchParams.append("endDate", endDate.toISOString());

    const response = await fetch(url.toString(), {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la récupération des logs par date:", error);
    throw error;
  }
};

// Créer un nouveau log d'inventaire
export const createInventoryLog = async (
  log: Omit<InventoryLog, "_id">
): Promise<InventoryLog> => {
  try {
    const response = await fetch(`${API_BASE_URL}/logs`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(log),
    });
    // Déconnexion si 401
    if (handleUnauthorizedError(response)) {
      throw new Error("Unauthorized");
    }
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Erreur lors de la création du log:", error);
    throw error;
  }
};

// Mettre à jour un log d'inventaire
export const updateInventoryLog = async (
  id: string,
  log: Partial<InventoryLog>
): Promise<InventoryLog> => {
  try {
    const response = await fetch(`${API_BASE_URL}/logs/${id}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(log),
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du log ${id}:`, error);
    throw error;
  }
};

// Supprimer un log d'inventaire
export const deleteInventoryLog = async (
  id: string
): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/logs/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Erreur lors de la suppression du log ${id}:`, error);
    throw error;
  }
};

// Obtenir le résumé de l'inventaire
export const getInventorySummary =
  async (): Promise<InventorySummaryResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/summary`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(
        "Erreur lors de la récupération du résumé d'inventaire:",
        error
      );
      throw error;
    }
  };

// Import déjà effectué en haut de fichier

// Obtenir les logs d'inventaire par ID d'emplacement
export const getInventoryLogsByLocationId = async (
  locationId: string
): Promise<InventoryLog[]> => {
  try {
    const url = new URL(`${API_BASE_URL}/logs`);
    url.searchParams.append("locationId", locationId);

    console.log("Récupération des logs pour l'emplacement:", locationId);
    console.log("URL:", url.toString());
    console.log("Headers:", getAuthHeaders());

    const response = await fetch(url.toString(), {
      headers: getAuthHeaders(),
    });

    // Vérifier si la réponse est 401 et gérer la déconnexion/redirection si nécessaire
    if (handleUnauthorizedError(response)) {
      throw new Error("Session expirée. Veuillez vous reconnecter.");
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Erreur HTTP ${response.status}:`, errorText);
      throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la récupération des logs pour l'emplacement ${locationId}:`,
      error
    );
    throw error;
  }
};

// Obtenir un log d'inventaire spécifique par son ID
export const getInventoryLogById = async (
  logId: string
): Promise<InventoryLog> => {
  try {
    const response = await fetch(`${API_BASE_URL}/logs/${logId}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Erreur lors de la récupération du log ${logId}:`, error);
    throw error;
  }
};

// Obtenir les logs d'inventaire entre deux dates pour un emplacement donné
export const getInventoryLogsByDateRange = async (
  locationId: string,
  startDate: Date,
  endDate: Date
): Promise<InventoryLog[]> => {
  try {
    const startDateStr = startDate.toISOString();
    const endDateStr = endDate.toISOString();
    const encodedLocationId = encodeURIComponent(locationId);

    const response = await fetch(
      `${API_BASE_URL}/logs-by-date-range?location=${encodedLocationId}&startDate=${startDateStr}&endDate=${endDateStr}`
    );

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la récupération des logs pour l'emplacement ${locationId} entre ${startDate} et ${endDate}:`,
      error
    );
    throw error;
  }
};

// Type pour un produit dans l'inventaire
export interface InventoryProduct {
  id: string; // ID Shopify du produit (format: gid://shopify/Product/XXXXX)
  title: string;
  vendor?: string;
  qte: number;
  price?: number;
  sku?: string;
}

// Reconstruire l'inventaire à une date précise
export const reconstructInventoryAtDate = async (
  locationId: string,
  targetDate: Date
): Promise<InventoryProduct[]> => {
  try {
    // Étape 1: Récupérer l'inventaire actuel depuis Shopify
    const currentInventory = await getProductsByLocation(locationId);

    // Créer une map des produits par ID Shopify (seul identifiant fiable)
    const productMapById = new Map<string, InventoryProduct>();

    currentInventory.forEach((product) => {
      // Convertir SimpleProduct en InventoryProduct
      productMapById.set(product.id, {
        id: product.id,
        title: product.title,
        qte: product.inventory || 0,
        price: product.price,
      });
    });

    // Étape 2: Récupérer tous les logs entre aujourd'hui et la date cible
    const now = new Date();
    const logs = await getInventoryLogsByDateRange(locationId, targetDate, now);

    // Étape 3: Traiter les logs en ordre chronologique inverse
    // Pour chaque log, annuler ses effets sur l'inventaire
    logs.reverse().forEach((log) => {
      if (!log.changes || log.changes.length === 0) return;

      log.changes.forEach((change) => {
        // Chercher le produit uniquement par ID Shopify qui est dans change.id
        let existingProduct = null;

        if (change.id) {
          existingProduct = productMapById.get(change.id);
        }

        // Si le produit n'existe pas encore, le créer avec l'ID Shopify comme identifiant principal
        if (!existingProduct && change.id) {
          existingProduct = {
            id: change.id, // ID Shopify est l'identifiant principal
            title: change.name || "Produit inconnu", // Utiliser name comme titre
            vendor: change.vendor,
            qte: 0,
            price: change.price,
            sku: change.sku,
          };

          // Ajouter à la map par ID
          productMapById.set(change.id, existingProduct);
        }

        if (existingProduct) {
          // Inverser l'effet du changement selon le type de log
          switch (log.type) {
            case "entry":
            case "revert":
              // Pour une entrée ou un revert, on retire les produits loggés
              existingProduct.qte -= change.qte;
              break;

            case "exit":
              // Si c'était une sortie, on ajoute les produits (qte est déjà négative)
              existingProduct.qte -= change.qte;
              break;

            case "inventory":
              // Pour un inventaire, il faut annuler la différence
              existingProduct.qte -= change.qte;
              break;
          }
        } else if (change.id) {
          console.warn(
            `Produit avec ID ${change.id} non trouvé dans l'inventaire actuel et impossible à créer.`
          );
        }
      });
    });

    // Étape 4: Récupérer toutes les ventes Shopify entre la date cible et aujourd'hui
    const targetDateStr = targetDate.toISOString();
    const nowStr = now.toISOString();

    try {
      const orders = await getOrdersByLocationAndDateRange(
        targetDateStr,
        nowStr,
        locationId
      );
      console.log(
        `${
          orders.length
        } commandes récupérées entre ${targetDate.toLocaleDateString()} et aujourd'hui`
      );

      // Pour chaque commande, ajouter les quantités vendues au stock
      orders.forEach((order) => {
        if (order.lineItems && Array.isArray(order.lineItems)) {
          order.lineItems.forEach((lineItem: OrderLineItem) => {
            // Si le produit a un variant avec un ID de produit Shopify
            if (lineItem.variant && lineItem.variant.product) {
              // Récupérer l'ID du produit Shopify
              const productId = lineItem.variant.product.id;

              // Chercher le produit uniquement par ID Shopify
              const matchingProduct = productMapById.get(productId);

              if (matchingProduct) {
                // Ajouter la quantité vendue au stock reconstituté
                // Car ce sont des ventes qui ont eu lieu après la date cible
                console.log(
                  `Ajout de ${lineItem.quantity} unités du produit ${matchingProduct.title} (ID: ${productId}) au stock reconstituté`
                );
                matchingProduct.qte += lineItem.quantity;
              } else {
                console.log(
                  `Produit non trouvé dans l'inventaire: ID ${productId}`
                );
              }
            }
          });
        }
      });
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des commandes Shopify:",
        error
      );
      // Continuer avec les données des logs uniquement si les ventes ne peuvent pas être récupérées
    }

    // Convertir la map en tableau de produits
    return Array.from(productMapById.values()).filter((p) => p.qte !== 0);
  } catch (error) {
    console.error(
      `Erreur lors de la reconstruction de l'inventaire à la date ${targetDate}:`,
      error
    );
    throw error;
  }
};
